import { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { rtdb } from "@/lib/firebase";
import type { ParsedQr } from "@/lib/qrParse";

export interface OrderHistoryEntry {
  createdAt?: number;
  products?: Array<{ id?: string; quantity?: number; unitPrice?: number; categoryId?: string }>;
  source?: string;
  cartSnapshotId?: string;
}

interface HistoryState {
  quantityHistory: Record<string, number>;
  categoryQuantityHistory: Record<string, number>;
  orderHistory: OrderHistoryEntry[];
  lastSentAt: number | null;
}

/**
 * Listens to the per-table RTDB counters. The same nodes are written by the
 * tablet (Cart.js:225-279) and by qrExecuteSubmit so both apps see the same
 * live state — including `lastSentAt` introduced in phase 2.
 */
export const useQuantityHistory = (parsed: ParsedQr | null): HistoryState => {
  const [state, setState] = useState<HistoryState>({
    quantityHistory: {},
    categoryQuantityHistory: {},
    orderHistory: [],
    lastSentAt: null,
  });

  useEffect(() => {
    if (!parsed) return;
    const qhRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/quantityHistory`);
    const cqhRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/categoryQuantityHistory`);
    const ohRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/orderHistory`);
    const lastRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/lastSentAt`);

    const u1 = onValue(qhRef, (snap) =>
      setState((s) => ({ ...s, quantityHistory: (snap.val() as Record<string, number>) || {} }))
    );
    const u2 = onValue(cqhRef, (snap) =>
      setState((s) => ({
        ...s,
        categoryQuantityHistory: (snap.val() as Record<string, number>) || {},
      }))
    );
    const u3 = onValue(ohRef, (snap) => {
      // RTDB stores orderHistory as a numerically-indexed object; turn into an array.
      const raw = snap.val() as Record<string, OrderHistoryEntry> | OrderHistoryEntry[] | null;
      let arr: OrderHistoryEntry[] = [];
      if (Array.isArray(raw)) {
        arr = raw.filter(Boolean);
      } else if (raw && typeof raw === "object") {
        arr = Object.keys(raw)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => raw[k as keyof typeof raw] as OrderHistoryEntry)
          .filter(Boolean);
      }
      setState((s) => ({ ...s, orderHistory: arr }));
    });
    const u4 = onValue(lastRef, (snap) =>
      setState((s) => ({ ...s, lastSentAt: (snap.val() as number | null) ?? null }))
    );
    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }, [parsed?.restoId, parsed?.qrId]);

  return state;
};
